import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TROPHY_CATALOG, TROPHY_CATEGORIES, getTrophyByKey } from "../../data/trophyCatalog";
import AvatarWithTrophy, { TrophyStarIcon } from "./AvatarWithTrophy";

const STROKE = 1.8;

function TrophyIcon({ trophy, size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={trophy.iconStroke || "#C4790A"} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 010-5H6" /><path d="M18 9h1.5a2.5 2.5 0 000-5H18" />
      <path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0012 0V2z" />
    </svg>
  );
}

export default function TrophyCaseTab({
  profileUser,
  isOwnProfile,
  avatarUrl,
  onPinChange,
  editPath = "/profile/edit",
}) {
  const [category, setCategory] = useState("all");
  const [modalTrophy, setModalTrophy] = useState(null);

  const earnedKeys = useMemo(() => {
    const keys = new Set([
      ...(profileUser?.earnedMilestones || []),
      ...(profileUser?.earnedTrophies || []),
    ]);
    return keys;
  }, [profileUser]);

  const pinnedKey = profileUser?.pinnedTrophy || null;
  const pinnedTrophy = pinnedKey ? getTrophyByKey(pinnedKey) : null;

  const visible = useMemo(() => {
    return TROPHY_CATALOG.filter((t) => {
      if (t.secret && !earnedKeys.has(t.key)) return false;
      if (category === "all") return true;
      return t.category === category;
    });
  }, [category, earnedKeys]);

  const earnedCount = TROPHY_CATALOG.filter((t) => earnedKeys.has(t.key)).length;
  const legendaryCount = TROPHY_CATALOG.filter((t) => earnedKeys.has(t.key) && t.rarity === "legendary").length;
  const lockedCount = TROPHY_CATALOG.filter((t) => !earnedKeys.has(t.key) && !t.secret).length;

  const privacy = profileUser?.privacy?.trophyCase ?? "public";
  if (!isOwnProfile && privacy === "private") {
    return (
      <div className="up-tc-empty">
        <p>This trophy case is private.</p>
      </div>
    );
  }

  return (
    <div className="up-tc-wrap">
      {isOwnProfile && pinnedTrophy && (
        <div className="up-tc-pinned-showcase">
          <div className="up-tc-ps-av-wrap">
            <AvatarWithTrophy
              src={avatarUrl}
              alt={profileUser?.displayName || "You"}
              imgClassName="up-tc-ps-av"
              size="lg"
              pinnedTrophy={pinnedKey}
            />
          </div>
          <div className="up-tc-ps-text">
            <div className="up-tc-ps-label">Currently pinned to your profile</div>
            <div className="up-tc-ps-name">{pinnedTrophy.name}</div>
            <div className="up-tc-ps-desc">Shown next to your avatar everywhere — comments, the Wall, your profile</div>
          </div>
          <Link to={editPath} className="up-tc-ps-change-btn">Change Pin</Link>
        </div>
      )}

      <div className="up-tc-stats-row">
        <div className="up-tcs"><div className="up-tcs-num">{earnedCount}</div><div className="up-tcs-lbl">Trophies earned</div></div>
        <div className="up-tcs"><div className="up-tcs-num">{legendaryCount}</div><div className="up-tcs-lbl">Legendary</div></div>
        <div className="up-tcs"><div className="up-tcs-num">{lockedCount}</div><div className="up-tcs-lbl">Locked — still to earn</div></div>
      </div>

      <div className="up-tc-filters">
        {TROPHY_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className={`up-tcf${category === cat.id ? " up-tcf--on" : ""}`}
            onClick={() => setCategory(cat.id)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="up-tc-grid">
        {visible.map((trophy) => {
          const earned = earnedKeys.has(trophy.key);
          const isPinned = pinnedKey === trophy.key;
          return (
            <button
              key={trophy.key}
              type="button"
              className={`up-tc-card${earned ? "" : " up-tc-card--locked"}${isPinned ? " up-tc-card--pinned" : ""}`}
              onClick={() => earned && setModalTrophy(trophy)}
              disabled={!earned}
            >
              {isPinned && (
                <span className="up-tc-pin-indicator"><TrophyStarIcon size={11} /></span>
              )}
              <div
                className={`up-tc-icon-wrap${earned && trophy.rarity === "legendary" ? " up-tc-icon-wrap--shine" : ""}`}
                style={{ background: earned ? trophy.iconBg : "var(--surface-2)" }}
              >
                <TrophyIcon trophy={trophy} />
              </div>
              <div className="up-tc-name">{trophy.name}</div>
              {earned ? (
                <span className={`up-tc-rarity up-tc-rarity--${trophy.rarity}`}>{trophy.rarity}</span>
              ) : (
                <span className="up-tc-locked-text">Not yet earned</span>
              )}
            </button>
          );
        })}
      </div>

      {modalTrophy && (
        <div className="up-tc-modal up-tc-modal--open" role="dialog" aria-modal="true">
          <button type="button" className="up-tc-backdrop" onClick={() => setModalTrophy(null)} aria-label="Close" />
          <div className="up-tc-modal-card">
            <button type="button" className="up-tc-close" onClick={() => setModalTrophy(null)} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={STROKE} strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="up-tc-modal-top">
              <div className="up-tc-modal-icon" style={{ background: modalTrophy.iconBg }}>
                <TrophyIcon trophy={modalTrophy} size={40} />
              </div>
              <h3 className="up-tc-modal-name">{modalTrophy.name}</h3>
              <span className={`up-tc-rarity up-tc-rarity--${modalTrophy.rarity}`}>{modalTrophy.rarity}</span>
            </div>
            <div className="up-tc-modal-body">
              <p className="up-tc-modal-desc">{modalTrophy.description}</p>
              {isOwnProfile && onPinChange && (
                <button
                  type="button"
                  className={`up-tc-pin-btn${pinnedKey === modalTrophy.key ? " up-tc-pin-btn--unpin" : ""}`}
                  onClick={() => {
                    onPinChange(pinnedKey === modalTrophy.key ? null : modalTrophy.key);
                    setModalTrophy(null);
                  }}
                >
                  {pinnedKey === modalTrophy.key ? "Unpin from profile" : "Pin to profile"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
